import { GameScreen } from "../ui/screens/game_screen/game_screen.js"; // поправ шлях під свій проєкт
import { PhysicsWorld } from "../physics/physics_world.js";
import { setBlockMassMultiplier } from "../physics/physics_masses.js";
import { SimulationContext, SimulationRunResult } from "./simulation_context.js";
import { yieldToBrowser } from "./yield_to_browser.js";
import { GameBot } from "./game_bot.js";
import { LevelPackService } from "../level_pack/level_pack_service.js";

export interface SimulationStats
{
  level: number;
  runs: number;
  wins: number;
  timeouts: number;
  avg_shots: number;
  median_shots: number;
  min_shots: number;
  max_shots: number;

  p50: number;
  p75: number;
  p90: number;

  real_time_seconds: number;

  /** shots → how many winning runs finished in exactly that many shots */
  histogram: Record<number, number>;

  /** shots → fraction of ALL runs that won using at most that many shots */
  pass_rate: Record<number, number>;
}

export class SimulationRunner
{
  private readonly FIXED_DT = 1 / 60;

  private readonly NO_PROGRESS_SIM_SECONDS = 300;

  private readonly PROGRESS_CHECK_STEPS = 600;

  private readonly STEPS_PER_YIELD = 300;

  private readonly HEARTBEAT_MS = 2000;

  private readonly HEARTBEAT_STEPS = 60;

  public async run(
    level: number,
    runs = 1000,
    save = true,
    on_progress?: ( done: number, total: number, run_sim_seconds?: number ) => void,
    gate?: ( done: number ) => Promise<void>
  ): Promise<SimulationStats>
  {
    await LevelPackService.ensureLevel( level );
    SimulationContext.enabled = true;
    // Whatever multiplier a server segment set earlier — sims measure base
    // masses. With the context enabled the setter pins everything to ×1.
    setBlockMassMultiplier( 1 );
    // PhysicsWorld.setFastMode( true );

    const shots: number[] = [];
    let wins = 0;
    let timeouts = 0;

    GameBot.resetAimStats();

    const started = performance.now();

    try
    {
      let last_report = 0;

      const report = ( done: number, run_sim_seconds?: number ) =>
      {
        last_report = performance.now();
        on_progress?.( done, runs, run_sim_seconds );
      };

      for ( let i = 0; i < runs; i++ )
      {
        const result = await this.runOne( level, ( sim_seconds ) =>
        {
          if ( performance.now() - last_report >= this.HEARTBEAT_MS )
            report( i, sim_seconds );
        } );

        if ( result.won )
        {
          wins++;
          shots.push( result.shots_used );
        }
        else
        {
          timeouts++;
        }

        if ( i === 0 || i + 1 === runs || performance.now() - last_report >= this.HEARTBEAT_MS )
          report( i + 1 );

        if ( ( i + 1 ) % 25 === 0 )
        {
          const avg = shots.length > 0
            ? ( shots.reduce( ( s, v ) => s + v, 0 ) / shots.length ).toFixed( 2 )
            : "-";

          console.log(
            `[Sim] ${i + 1}/${runs} | сер. пострілів: ${avg} | ` +
            `${( ( performance.now() - started ) / 1000 ).toFixed( 1 )}s`
          );
        }

        // даємо браузеру подихати між прогонами (без тротлінгу у фонових вкладках)
        if ( ( i + 1 ) % 20 === 0 )
          await yieldToBrowser();

        if ( gate )
          await gate( i + 1 );
      }
    }
    finally
    {
      SimulationContext.enabled = false;
      // PhysicsWorld.setFastMode( false );
    }

    shots.sort( ( a, b ) => a - b );

    const stats: SimulationStats = {
      level,
      runs,
      wins,
      timeouts,
      avg_shots: shots.length > 0
        ? shots.reduce( ( s, v ) => s + v, 0 ) / shots.length
        : 0,
      median_shots: shots.length > 0
        ? shots[Math.floor( shots.length / 2 )]
        : 0,
      min_shots: shots[0] ?? 0,
      max_shots: shots[shots.length - 1] ?? 0,

      p50: this.percentile( shots, 0.50 ),
      p75: this.percentile( shots, 0.75 ),
      p90: this.percentile( shots, 0.90 ),

      real_time_seconds: ( performance.now() - started ) / 1000,

      histogram: {},
      pass_rate: {},
    };

    stats.histogram = this.buildHistogram( shots );
    stats.pass_rate = this.buildPassRate( stats, shots );

    console.log( "[Sim] Результат:", stats );

    const aim = GameBot.aimStats();
    console.log(
      `[Sim] Прицілювання: ${aim.picks} виборів, ${aim.blocked} відкинуто як недосяжні ` +
      `(${( aim.share * 100 ).toFixed( 1 )}%)`
    );

    console.log( `[Sim] Скільки % пройде рівень з N м'ячиками:` );

    for ( let n = stats.min_shots; n <= stats.max_shots; n++ )
    {
      const passed = shots.filter( s => s <= n ).length;
      console.log( `  ${String( n ).padStart( 3 )} → ${( passed / runs * 100 ).toFixed( 0 )}%` );
    }

    if ( save )
      this.saveJson( stats, shots );

    return stats;
  }

  public async runRange(
    from: number,
    to: number,
    runs = 300,
    save = true
  ): Promise<Array<{ level: number; min_shots: number }>>
  {
    const results: Array<{ level: number; min_shots: number }> = [];

    for ( let level = from; level <= to; level++ )
    {
      const stats = await this.run( level, runs, false );

      results.push( { level, min_shots: stats.min_shots } );

      console.log( `[Sim] рівень ${level}: min ${stats.min_shots}` );
    }

    if ( save )
      this.saveRangeJson( from, to, runs, results );

    return results;
  }

  private saveRangeJson(
    from: number,
    to: number,
    runs: number,
    results: Array<{ level: number; min_shots: number }>
  ): void
  {
    const json = JSON.stringify( {
      generated_at: new Date().toISOString(),
      runs_per_level: runs,
      levels: results,
    }, null, 2 );

    const blob = new Blob( [json], { type: "application/json" } );
    const url = URL.createObjectURL( blob );

    const link = document.createElement( "a" );
    link.href = url;
    link.download = `sim_levels_${from}-${to}.json`;
    link.click();

    URL.revokeObjectURL( url );
  }

  private percentile( sorted: number[], p: number ): number
  {
    if ( sorted.length === 0 )
      return 0;

    const index = Math.min(
      sorted.length - 1,
      Math.floor( sorted.length * p )
    );

    return sorted[index];
  }

  private async runOne( level: number, on_step?: ( sim_seconds: number ) => void ): Promise<SimulationRunResult>
  {
    const screen = new GameScreen();

    let result: SimulationRunResult = {
      won: false,
      shots_used: 0,
      sim_seconds: 0
    };

    screen.enableSimulation( ( r: SimulationRunResult ) =>
    {
      result = r;
    } );

    await screen.init( { level } );

    const no_progress_steps = Math.floor( this.NO_PROGRESS_SIM_SECONDS / this.FIXED_DT );

    let steps = 0;
    let last_targets = screen.getAliveTargets().length;
    let last_progress_step = 0;

    while ( !screen.isFinished() )
    {
      PhysicsWorld.update( this.FIXED_DT );
      screen.update( this.FIXED_DT );

      steps++;

      if ( steps % this.PROGRESS_CHECK_STEPS === 0 )
      {
        const targets = screen.getAliveTargets().length;

        if ( targets < last_targets )
        {
          last_targets = targets;
          last_progress_step = steps;
        }
        else if ( steps - last_progress_step >= no_progress_steps )
          break;
      }

      if ( steps % this.STEPS_PER_YIELD === 0 )
        await yieldToBrowser();

      if ( steps % this.HEARTBEAT_STEPS === 0 )
        on_step?.( steps * this.FIXED_DT );
    }

    result.sim_seconds = steps * this.FIXED_DT;

    screen.destroy();

    return result;
  }

  private saveJson( stats: SimulationStats, shots: number[] ): void
  {
    const data = {
      generated_at: new Date().toISOString(),
      level: stats.level,
      runs: stats.runs,
      wins: stats.wins,
      timeouts: stats.timeouts,
      avg_shots: Number( stats.avg_shots.toFixed( 2 ) ),
      min_shots: stats.min_shots,
      max_shots: stats.max_shots,
      p50: stats.p50,
      p75: stats.p75,
      p90: stats.p90,
      real_time_seconds: Number( stats.real_time_seconds.toFixed( 1 ) ),

      pass_rate: stats.pass_rate,
      histogram: stats.histogram,
      raw_shots: shots,
    };

    const json = JSON.stringify( data, null, 2 );

    const blob = new Blob( [json], { type: "application/json" } );
    const url = URL.createObjectURL( blob );

    const link = document.createElement( "a" );
    link.href = url;
    link.download = `sim_level_${stats.level}_${Date.now()}.json`;
    link.click();

    URL.revokeObjectURL( url );

    console.log( `[Sim] Збережено sim_level_${stats.level}.json` );
  }

  private buildPassRate( stats: SimulationStats, shots: number[] ): Record<number, number> 
  {
    const result: Record<number, number> = {};

    for ( let n = stats.min_shots; n <= stats.max_shots; n++ )
    {
      const passed = shots.filter( s => s <= n ).length;
      result[n] = Number( ( passed / stats.runs ).toFixed( 3 ) );
    }

    return result;
  }

  private buildHistogram( shots: number[] ): Record<number, number>
  {
    const result: Record<number, number> = {};

    for ( const s of shots )
      result[s] = ( result[s] ?? 0 ) + 1;

    return result;
  }
}